import { useCallback, useEffect, useRef, useState, type FocusEvent, type RefObject } from "react";

export interface PhoneMenu {
    /** Whether the menu is open. */
    open: boolean;
    openMenu: () => void;
    closeMenu: () => void;
    /** For the button that opens the menu: where the focus goes back to. */
    openerRef: RefObject<HTMLButtonElement | null>;
    /** For that button's tooltip trigger: keeps the tooltip shut for a focus that was handed back. */
    onOpenerFocus: (event: FocusEvent<HTMLElement>) => void;
}

/**
 * The phone's menu as the shell holds it: whether it is open, and the focus
 * its opener gets back when it closes.
 *
 * Below `md` the rail is a menu over the page (`RailFrame`), opened by a
 * button in the page's top bar. The menu takes the focus as it opens. When it
 * closes, the focus it held is gone with it, and a keyboard is left at the top
 * of the document, a screen reader wherever it falls. So it is handed back to
 * the button that opened the menu, by the shell: the button is the shell's,
 * outside the rail, and the menu is drawn by the rail or by what stands in for
 * it, which know nothing of it.
 *
 * - **Named, not remembered.** The button is the one this hook is given a ref
 *   to, not whatever held the focus as the menu opened: Safari does not focus
 *   a button that is clicked or tapped, so on an iPhone, the menu's home,
 *   nothing would have been remembered.
 * - **Only a focus that went with the menu.** If something holds it by then,
 *   it is left there. "Ask Optivo", asked for from the menu, closes the menu
 *   and puts the caret in its own window in the same breath; the discard guard
 *   opens its dialog the same way.
 * - **A moment later, not at once.** A mouse pressed beside the menu closes it
 *   on the way down, and the same press then takes the focus off whatever
 *   holds it, which is the browser's own doing for a press that lands on
 *   nothing focusable. Handed back as the menu went, the focus was on the
 *   page's body again before the button came up (measured in Chromium). So it
 *   waits for the next task, when the press has had its way.
 * - **Without the tooltip.** The opener explains itself in a tooltip, and a
 *   tooltip opens for any focus that no press brought. A focus handed back is
 *   one: it would show the tooltip to a finger that had just tapped the X, and
 *   to a pointer resting somewhere else. `onOpenerFocus` keeps it shut for
 *   that one focus. The tooltip is for a control arrived at; on Tab, and under
 *   the pointer, it opens as before.
 * - **Whatever closed it**: the X, Escape, a press beside the menu, a
 *   destination picked in it, Back and Forward, the window widened past `md`
 *   (where the button is not drawn, and the focus stays where it is).
 */
export function usePhoneMenu(): PhoneMenu {
    const [open, setOpen] = useState(false);
    const openerRef = useRef<HTMLButtonElement>(null);
    const wasOpen = useRef(false);
    const handingFocusBack = useRef(false);

    useEffect(() => {
        const closedJustNow = wasOpen.current && !open;
        wasOpen.current = open;
        if (!closedJustNow) return;
        const handBack = window.setTimeout(() => {
            // A focus that went with the menu is on the page's body by now.
            const holder = document.activeElement;
            if (holder && holder !== document.body) return;
            handingFocusBack.current = true;
            openerRef.current?.focus({ preventScroll: true });
            handingFocusBack.current = false;
        }, 0);
        // Opened again before then, the menu takes the focus itself.
        return () => window.clearTimeout(handBack);
    }, [open]);

    const openMenu = useCallback(() => setOpen(true), []);
    const closeMenu = useCallback(() => setOpen(false), []);
    // The tooltip's own handler runs after this one, and not at all for an
    // event this one has marked as handled.
    const onOpenerFocus = useCallback((event: FocusEvent<HTMLElement>) => {
        if (handingFocusBack.current) event.preventDefault();
    }, []);

    return { open, openMenu, closeMenu, openerRef, onOpenerFocus };
}
